/**
 * SLA / OLA timer computation for the conversation detail view.
 *
 * The backend gives us due timestamps (`firstResponseDueAt`, `resolutionDueAt`)
 * plus completion markers (`firstRespondedAt`, `resolvedAt`) and an overall
 * `slaState`. From those we derive a per-timer state + remaining/overdue label.
 */
import { timestampMs } from '../../../utils/timezone.js'

export type SlaTimerState = 'met' | 'running' | 'approaching' | 'breached' | 'none'

export interface SlaTimer {
  state: SlaTimerState
  /** ms remaining until due (positive) or overdue (negative). null when no due date. */
  deltaMs: number | null
  /** Human label for the remaining/overdue amount, e.g. "12m left", "2h overdue". */
  label: string
  /** Status badge label: MET, RUNNING, APPROACHING, BREACHED, etc. */
  statusLabel: string
  tone: {
    text: string
    bg: string
    bar: string
    dot: string
    border: string
  }
  /** Elapsed ms since start (or since completed for met timers). */
  elapsedMs: number
  /** Budget ms (target duration). */
  budgetMs: number
  /** Progress fraction 0..1. */
  progress: number
  /** Whether timer is near breach (within 20% of deadline). */
  isNearBreach: boolean
}

const TONES: Record<SlaTimerState, SlaTimer['tone']> = {
  met: { text: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'bg-gradient-to-r from-emerald-400 to-emerald-600', dot: 'bg-emerald-500', border: 'border-emerald-300' },
  running: { text: 'text-blue-700', bg: 'bg-blue-50', bar: 'bg-gradient-to-r from-blue-400 to-blue-600', dot: 'bg-blue-500', border: 'border-blue-300' },
  approaching: { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-gradient-to-r from-amber-400 to-orange-500', dot: 'bg-amber-500', border: 'border-amber-300' },
  breached: { text: 'text-red-700', bg: 'bg-red-50', bar: 'bg-gradient-to-r from-red-500 to-red-600', dot: 'bg-red-500', border: 'border-red-300' },
  none: { text: 'text-slate-500', bg: 'bg-slate-50', bar: 'bg-slate-300', dot: 'bg-slate-400', border: 'border-slate-200' },
}

/** Progress-based tones — as elapsed time grows, color shifts green → blue → amber → red. */
const PROGRESS_TONES: Array<{ threshold: number; tone: SlaTimer['tone'] }> = [
  { threshold: 0.5, tone: { text: 'text-emerald-700', bg: 'bg-emerald-50', bar: 'bg-gradient-to-r from-emerald-400 to-teal-500', dot: 'bg-emerald-500', border: 'border-emerald-300' } },
  { threshold: 0.75, tone: { text: 'text-blue-700', bg: 'bg-blue-50', bar: 'bg-gradient-to-r from-blue-400 to-indigo-500', dot: 'bg-blue-500', border: 'border-blue-300' } },
  { threshold: 0.9, tone: { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-gradient-to-r from-amber-400 to-orange-500', dot: 'bg-amber-500', border: 'border-amber-300' } },
  { threshold: 1.01, tone: { text: 'text-orange-700', bg: 'bg-orange-50', bar: 'bg-gradient-to-r from-orange-500 to-red-500', dot: 'bg-orange-500', border: 'border-orange-300' } },
]

/** OLA-specific tones — violet/purple base to distinguish from SLA blue. */
const OLA_PROGRESS_TONES: Array<{ threshold: number; tone: SlaTimer['tone'] }> = [
  { threshold: 0.5, tone: { text: 'text-violet-700', bg: 'bg-violet-50', bar: 'bg-gradient-to-r from-violet-400 to-purple-500', dot: 'bg-violet-500', border: 'border-violet-300' } },
  { threshold: 0.75, tone: { text: 'text-purple-700', bg: 'bg-purple-50', bar: 'bg-gradient-to-r from-purple-400 to-fuchsia-500', dot: 'bg-purple-500', border: 'border-purple-300' } },
  { threshold: 0.9, tone: { text: 'text-amber-700', bg: 'bg-amber-50', bar: 'bg-gradient-to-r from-amber-400 to-orange-500', dot: 'bg-amber-500', border: 'border-amber-300' } },
  { threshold: 1.01, tone: { text: 'text-orange-700', bg: 'bg-orange-50', bar: 'bg-gradient-to-r from-orange-500 to-red-500', dot: 'bg-orange-500', border: 'border-orange-300' } },
]

function progressTone(progress: number, isOla: boolean): SlaTimer['tone'] {
  const tones = isOla ? OLA_PROGRESS_TONES : PROGRESS_TONES
  for (const t of tones) {
    if (progress < t.threshold) return t.tone
  }
  return tones[tones.length - 1]!.tone
}

/** Under this many ms remaining, a running timer is flagged "approaching". */
const APPROACHING_THRESHOLD_MS = 30 * 60 * 1000

export function humanizeDuration(ms: number): string {
  const abs = Math.abs(ms)
  const mins = Math.floor(abs / 60000)
  const hours = Math.floor(abs / 3600000)
  const days = Math.floor(abs / 86400000)
  if (days >= 1) {
    const remHours = Math.floor((abs - days * 86400000) / 3600000)
    return remHours > 0 ? `${days}d ${remHours}h` : `${days}d`
  }
  if (hours >= 1) {
    const remMins = Math.floor((abs - hours * 3600000) / 60000)
    return remMins > 0 ? `${hours}h ${remMins}m` : `${hours}h`
  }
  if (mins >= 1) {
    const secs = Math.floor((abs - mins * 60000) / 1000)
    return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`
  }
  const secs = Math.floor(abs / 1000)
  return `${secs}s`
}

export function computeSlaTimer(
  dueAt: string | null,
  completedAt: string | null,
  nowMs: number,
  startedAt?: string | null,
  targetMinutes?: number | null,
  isOla?: boolean,
): SlaTimer {
  if (!dueAt) {
    return {
      state: 'none', deltaMs: null, label: 'No target', statusLabel: 'NONE',
      tone: TONES.none, elapsedMs: 0, budgetMs: 0, progress: 0, isNearBreach: false,
    }
  }
  const due = timestampMs(dueAt)
  const start = startedAt ? timestampMs(startedAt) : due - (targetMinutes ? targetMinutes * 60_000 : 0)
  const budgetMs = targetMinutes ? targetMinutes * 60_000 : Math.max(1, due - start)

  if (completedAt) {
    const done = timestampMs(completedAt)
    const onTime = done <= due
    const elapsedMs = Math.max(0, done - start)
    return {
      state: 'met',
      deltaMs: due - done,
      label: onTime ? 'Met on time' : `Met ${humanizeDuration(done - due)} late`,
      statusLabel: onTime ? 'MET' : 'BREACHED',
      tone: onTime ? TONES.met : TONES.breached,
      elapsedMs,
      budgetMs,
      progress: Math.min(1, elapsedMs / budgetMs),
      isNearBreach: false,
    }
  }

  const delta = due - nowMs
  const elapsedMs = Math.max(0, nowMs - start)
  const progress = Math.min(1, elapsedMs / budgetMs)

  if (delta <= 0) {
    return {
      state: 'breached',
      deltaMs: delta,
      label: `${humanizeDuration(delta)} overdue`,
      statusLabel: 'BREACHED',
      tone: TONES.breached,
      elapsedMs,
      budgetMs,
      progress: 1,
      isNearBreach: false,
    }
  }
  const isNearBreach = delta < budgetMs * 0.2
  const state: SlaTimerState = delta <= APPROACHING_THRESHOLD_MS || isNearBreach ? 'approaching' : 'running'
  // Use progress-based tone so color shifts as time elapses (green → blue → amber → red)
  // instead of always blue for "running". OLA timers get violet/purple tones.
  const tone = progressTone(progress, isOla ?? false)
  return {
    state,
    deltaMs: delta,
    label: `${humanizeDuration(delta)} left`,
    statusLabel: state === 'approaching' ? 'APPROACHING' : 'RUNNING',
    tone,
    elapsedMs,
    budgetMs,
    progress,
    isNearBreach,
  }
}

/**
 * Progress fraction (0..1) for a timer bar. Uses elapsed/budget ratio when
 * available, otherwise falls back to a 24h window approximation.
 */
export function slaProgress(timer: SlaTimer): number {
  if (timer.progress > 0) return timer.progress
  if (timer.state === 'met') return 1
  if (timer.state === 'breached') return 1
  if (timer.deltaMs === null) return 0
  const windowMs = 24 * 60 * 60 * 1000
  const remaining = Math.min(timer.deltaMs, windowMs)
  return Math.max(0, Math.min(1, 1 - remaining / windowMs))
}
